abstract class Miscellanious {
  /**
   * Groups an array of items by a given key.
   * @param list - The array of items to group.
   * @param getKey - The function to get the key for each item.
   * @returns An object with the grouped items.
   */
  public static groupBy = <T, K extends keyof any>(
    list: T[],
    getKey: (item: T) => K
  ) =>
    list?.reduce((previous, currentItem) => {
      const group = getKey(currentItem);
      if (!previous[group]) previous[group] = [];
      previous[group].push(currentItem);
      return previous;
    }, {} as Record<K, T[]>);

  /**
   * Returns the currency symbol for a given locale and currency code.
   * @param locale - The locale to use for the currency symbol.
   * @param currency - The currency code to get the symbol for.
   * @returns The currency symbol.
   */
  public static currencySymbolFromCode = (locale: string, currency: string) => {
    if (!currency) return '';
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    });

    let symbol;
    formatter.formatToParts(0).forEach(({ type, value }) => {
      if (type === 'currency') {
        symbol = value;
      }
    });

    return symbol;
  };

    /**
   * Attempts to fix mojibake caused by UTF-8 bytes misinterpreted as Latin-1 or Windows-1252.
   * E.g., "Ð‘ÐŸ" → "БП"
   */
  public static fixUtf8Mojibake(input: string): string {
    try {
      // Step 1: Convert the string back to the original byte sequence (as Latin-1)
      const bytes = new Uint8Array(input.length);
      for (let i = 0; i < input.length; i++) {
        bytes[i] = input.charCodeAt(i);
      }

      // Step 2: Decode those bytes properly as UTF-8
      const decoder = new TextDecoder("utf-8");
      return decoder.decode(bytes);
    } catch (e) {
      console.error("Failed to decode:", e);
      return input;
    }
  }

  /**
   * Extracts and fixes the filename from a URL.
   * @param url - The URL to extract the filename from.
   * @returns The fixed filename.
   */
  public static extractAndFixFilenameFromUrl(url: string): string {
    // 1. Extract filename from URL
    const match = url.match(/\/([^\/?#]+\.pdf)(?:[?#]|$)/i);
    if (!match) return "unknown.pdf";

    const encodedFilename = match[1];

    // 2. Decode percent encoding
    const decoded = decodeURIComponent(encodedFilename);

    // 3. Fix mojibake
    const fixed = Miscellanious.fixUtf8Mojibake(decoded);
    return fixed;
  }

  /**
   * Inserts <wbr> tags every {chars} characters to allow custom line breaks.
   * @param input - The string to insert <wbr> tags into.
   * @param chars - The number of characters to insert a <wbr> tag after.
   * @returns The string with <wbr> tags inserted at the specified intervals.
   */
  public static makeStringCustomBreak(input: string, chars: number): string {
    const chunks: string[] = [];
    for (let i = 0; i < input.length; i += chars) {
      chunks.push(input.slice(i, i + chars));
    }
    return chunks.join('<wbr>');
  }
}


abstract class NumberUtils {

  /**
   * Returns the rounded percentage of a fraction over a total.
   * @param fraction - The fraction to get the percentage of.
   * @param total - The total to divide the fraction by.
   * @returns The rounded percentage.
   */
  public static getRoundedPercentage(fraction: number, total: number) {
    return total === 0 ? 0 : Number(((fraction * 100) / total).toFixed(2));
  }
}

export { Miscellanious, NumberUtils };
